import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadFile, useAddTake, useApi } from '../../api/hooks';
import { formatDuration } from '../../lib/format';
import { useToast } from '../../ui/Toast';
import {
  baseContentType,
  describeMediaError,
  extensionFor,
  openCamera,
  pickMimeType,
  recordSource,
  requestWakeLock,
  stopStream,
  type RecordSource,
} from './media';

type Phase = 'starting' | 'ready' | 'countdown' | 'recording' | 'review' | 'saving' | 'error';
type Mode = 'line' | 'roll';

interface Props {
  reelId: string;
  orgId: string;
  /** Spoken text of every line, in order. Stage directions in [brackets] show small and are not read. */
  lines: string[];
  onClose: () => void;
  /** Duration of each countdown number (ms). */
  countdownStepMs?: number;
}

interface Recorded {
  blob: Blob;
  url: string;
  durationMs: number;
  peakDb: number | null;
  size?: { w: number; h: number };
}

const PREFS = 'rm.prompter';
function readPrefs(): { mode: Mode; fs: number; speed: number } {
  try {
    const p = JSON.parse(localStorage.getItem(PREFS) ?? '{}') as Partial<{ mode: Mode; fs: number; speed: number }>;
    return { mode: p.mode === 'roll' ? 'roll' : 'line', fs: p.fs ?? 34, speed: p.speed ?? 40 };
  } catch {
    return { mode: 'line', fs: 34, speed: 40 };
  }
}
function savePrefs(p: { mode: Mode; fs: number; speed: number }) {
  try {
    localStorage.setItem(PREFS, JSON.stringify(p));
  } catch {
    /* private mode */
  }
}

/** Text with [stage directions] as small yellow cues. */
function Cued({ text }: { text: string | undefined }) {
  return (
    <>
      {String(text ?? '')
        .split(/(\[[^\]]*\])/)
        .filter(Boolean)
        .map((part, i) =>
          part.startsWith('[') ? (
            <span key={i} className="cu">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          ),
        )}
    </>
  );
}

const SILENT_DB = -45;

/**
 * Full-screen recording studio (ported from the BTSQ teleprompter): camera preview behind a reading band, line by
 * line (tap or arrows) or as a running roll, mic level, framing guide, countdown, then a review of the take before
 * it is uploaded.
 */
export function Teleprompter({ reelId, orgId, lines, onClose, countdownStepMs = 750 }: Props) {
  const api = useApi();
  const toast = useToast();
  const addTake = useAddTake(reelId);
  const [phase, setPhase] = useState<Phase>('starting');
  const [problem, setProblem] = useState<string | null>(null);
  const [count, setCount] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [prefs, setPrefs] = useState(readPrefs);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [facing, setFacing] = useState<'user' | 'environment'>('user');
  const [level, setLevel] = useState(0);
  const [take, setTake] = useState<Recorded | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const sourceRef = useRef<RecordSource | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const discard = useRef(false);
  const peak = useRef(-120);
  const analyser = useRef<AnalyserNode | null>(null);
  const preview = useRef<HTMLVideoElement>(null);
  const rollRef = useRef<HTMLDivElement>(null);
  const rollY = useRef(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const setPref = (p: Partial<typeof prefs>) =>
    setPrefs((old) => {
      const next = { ...old, ...p };
      savePrefs(next);
      return next;
    });

  // Camera (and mic meter) for the chosen side; wake lock for the lifetime of the studio.
  useEffect(() => {
    let alive = true;
    let ctx: AudioContext | null = null;
    let raf = 0;
    setPhase((p) => (p === 'review' ? p : 'starting'));
    openCamera(facing)
      .then((stream) => {
        if (!alive) return stopStream(stream);
        streamRef.current = stream;
        sourceRef.current?.stop();
        sourceRef.current = null;
        const view = preview.current;
        if (view) {
          view.srcObject = stream;
          void view.play?.()?.catch?.(() => {});
        }
        try {
          const AC = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
          if (AC && stream.getAudioTracks?.().length) {
            ctx = new AC();
            const a = ctx.createAnalyser();
            a.fftSize = 1024;
            ctx.createMediaStreamSource(stream).connect(a);
            analyser.current = a;
            const buf = new Float32Array(a.fftSize);
            const step = () => {
              a.getFloatTimeDomainData(buf);
              let max = 0;
              for (const v of buf) max = Math.max(max, Math.abs(v));
              const db = max > 0 ? 20 * Math.log10(max) : -120;
              if (recorderRef.current?.state === 'recording') peak.current = Math.max(peak.current, db);
              setLevel(Math.max(0, Math.min(1, (db + 60) / 60)));
              raf = requestAnimationFrame(step);
            };
            raf = requestAnimationFrame(step);
          }
        } catch {
          analyser.current = null;
        }
        setPhase((p) => (p === 'review' ? p : 'ready'));
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setProblem(describeMediaError(e));
        setPhase('error');
      });
    return () => {
      alive = false;
      cancelAnimationFrame?.(raf);
      void ctx?.close().catch(() => {});
      analyser.current = null;
      if (recorderRef.current?.state === 'recording') {
        discard.current = true;
        recorderRef.current.stop();
      }
      sourceRef.current?.stop();
      sourceRef.current = null;
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, [facing]);

  useEffect(() => {
    let lock: Awaited<ReturnType<typeof requestWakeLock>> = null;
    let alive = true;
    void requestWakeLock().then((l) => {
      if (alive) lock = l;
      else void l?.release().catch(() => {});
    });
    closeRef.current?.focus();
    return () => {
      alive = false;
      void lock?.release().catch(() => {});
    };
  }, []);

  const takeUrl = take?.url;
  useEffect(
    () => () => {
      if (takeUrl) URL.revokeObjectURL?.(takeUrl);
    },
    [takeUrl],
  );

  // Countdown 3, 2, 1, then record.
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (count <= 0) {
      startRecording();
      return;
    }
    const t = setTimeout(() => setCount((c) => c - 1), countdownStepMs);
    return () => clearTimeout(t);
  }, [phase, count, countdownStepMs]);

  // Clock while recording; the roll runs at the chosen speed.
  useEffect(() => {
    if (phase !== 'recording') return;
    let last = 0;
    let raf = 0;
    const tick = (ts: number) => {
      const dt = last ? (ts - last) / 1000 : 0;
      last = ts;
      setElapsed(Date.now() - startedAt.current);
      if (prefsRef.current.mode === 'roll' && !pausedRef.current) moveRoll(rollY.current + prefsRef.current.speed * dt);
      raf = requestAnimationFrame(tick);
    };
    if (typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame?.(raf);
  }, [phase]);

  /** Roll position in px; the line at the reading mark becomes the current line. */
  function moveRoll(y: number) {
    const el = rollRef.current;
    if (!el) return;
    rollY.current = Math.max(0, Math.min(el.scrollHeight, y));
    el.style.transform = `translateY(${-rollY.current}px)`;
    let cur = 0;
    Array.from(el.children).forEach((p, i) => {
      if ((p as HTMLElement).offsetTop <= rollY.current + 4) cur = i;
    });
    setIdx((old) => (old === cur ? old : cur));
  }

  const go = useCallback(
    (d: number) => {
      const n = Math.max(0, Math.min(lines.length - 1, idx + d));
      if (prefs.mode === 'roll') {
        const p = rollRef.current?.children[n] as HTMLElement | undefined;
        moveRoll(p ? p.offsetTop : 0);
      }
      setIdx(n);
    },
    [idx, lines.length, prefs.mode],
  );

  const save = useCallback(async () => {
    if (!take) return;
    setPhase('saving');
    try {
      const contentType = baseContentType(take.blob.type || recorderRef.current?.mimeType || '');
      const up = await uploadFile(
        api,
        orgId,
        { kind: 'take', contentType, name: `take.${extensionFor(contentType)}`, reelId },
        take.blob,
      );
      const detail = await addTake.mutateAsync({
        uploadId: up.uploadId,
        durationMs: Math.max(0, Math.round(take.durationMs)),
      });
      const n = detail.reel.takes.reduce((m, t) => Math.max(m, t.n), 0);
      toast(`Take ${n} saved`);
      onClose();
    } catch (e) {
      setProblem(`The take could not be saved. ${e instanceof Error ? e.message : ''}`.trim());
      setPhase('review');
    }
  }, [api, orgId, reelId, addTake, toast, onClose, take]);

  function startRecording() {
    const camera = streamRef.current;
    if (!camera) return;
    try {
      // decided on the shown picture (see recordSource): portrait stays as it is, a landscape picture on a phone
      // held upright is recorded as the 9:16 middle that the preview shows
      if (!sourceRef.current && preview.current && preview.current.videoWidth > 0) {
        sourceRef.current = recordSource(camera, preview.current);
        console.info(JSON.stringify({ msg: 'recording source', mode: sourceRef.current.mode }));
      }
      const stream = sourceRef.current?.stream ?? camera;
      const mimeType = pickMimeType();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunks.current = [];
      discard.current = false;
      peak.current = -120;
      rec.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) chunks.current.push(e.data);
      };
      rec.onstop = () => {
        if (discard.current) return;
        const durationMs = Date.now() - startedAt.current;
        const blob = new Blob(chunks.current, { type: rec.mimeType || mimeType || 'video/webm' });
        const url = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : '';
        setTake({ blob, url, durationMs, peakDb: analyser.current ? peak.current : null });
        setPhase('review');
      };
      recorderRef.current = rec;
      startedAt.current = Date.now();
      rec.start(1000);
      setElapsed(0);
      setPaused(false);
      setPhase('recording');
    } catch (e) {
      setProblem(`Recording could not start. ${e instanceof Error ? e.message : ''}`.trim());
      setPhase('ready');
    }
  }

  const onRec = () => {
    if (phase === 'ready') {
      setProblem(null);
      setIdx(0);
      moveRoll(0);
      setCount(3);
      setPhase('countdown');
    } else if (phase === 'recording') {
      recorderRef.current?.stop();
    }
  };

  const again = () => {
    setTake(null);
    setProblem(null);
    setIdx(0);
    moveRoll(0);
    setPhase(streamRef.current ? 'ready' : 'starting');
  };

  const recording = phase === 'recording';
  const busy = phase === 'saving';
  const warnings: string[] = [];
  if (take?.peakDb != null && take.peakDb < SILENT_DB)
    warnings.push(
      `There is hardly any sound on this take (loudest ${Math.round(take.peakDb)} dB). Check the microphone and record again.`,
    );
  if (take?.size && take.size.w > take.size.h)
    warnings.push('This take is landscape. For the reel, hold the phone upright.');

  return (
    <div
      className={`studio ${recording ? 'recording' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Teleprompter"
      style={{ ['--fs' as string]: `${prefs.fs}px` }}
      onKeyDown={(e) => {
        if (phase === 'review') return;
        if (['ArrowRight', 'ArrowDown', 'PageDown', ' ', 'Enter'].includes(e.key) && e.target === e.currentTarget) {
          e.preventDefault();
          go(1);
        } else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(e.key)) {
          e.preventDefault();
          go(-1);
        } else if (e.key === 'Escape' && !recording && !busy) onClose();
      }}
      tabIndex={-1}
    >
      <video
        ref={preview}
        className={`preview ${facing === 'user' ? 'mirror' : ''}`}
        muted
        playsInline
        autoPlay
        aria-label="Camera preview"
        hidden={phase === 'review' || phase === 'saving'}
      />

      {phase === 'review' || phase === 'saving' ? (
        <div className="review">
          <video
            className="player"
            src={take?.url}
            controls
            playsInline
            aria-label="Recorded take"
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              if (take && v.videoWidth) setTake({ ...take, size: { w: v.videoWidth, h: v.videoHeight } });
            }}
          />
          <p className="takeinfo">
            {take ? formatDuration(take.durationMs) : ''}
            {take?.size ? ` · ${take.size.w}×${take.size.h}` : ''}
          </p>
          {(warnings.length > 0 || problem) && (
            <p className="status warn" role="alert">
              {[...warnings, problem].filter(Boolean).join(' ')}
            </p>
          )}
          <div className="row">
            <button type="button" className="btn sec" disabled={busy} onClick={again}>
              Record again
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving take…' : 'Use take'}
            </button>
          </div>
          <button type="button" className="linkbtn" disabled={busy} onClick={onClose}>
            Discard and close
          </button>
        </div>
      ) : (
        <>
          <div
            className="band"
            aria-live="polite"
            onClick={() => {
              if (prefs.mode === 'line') go(1);
              else if (recording) setPaused((p) => !p);
            }}
          >
            {prefs.mode === 'line' ? (
              <div>
                <p className="cur">
                  <Cued text={lines[idx]} />
                </p>
                <p className="next">
                  <Cued text={lines[idx + 1]} />
                </p>
                {recording && idx === lines.length - 1 && <p className="endhint">Last line. Then press stop.</p>}
              </div>
            ) : (
              <>
                <div className="roll" ref={rollRef}>
                  {lines.map((l, i) => (
                    <p key={i} className={i === idx ? 'on' : undefined}>
                      <Cued text={l} />
                    </p>
                  ))}
                </div>
                <span className="readline" aria-hidden="true" />
                {paused && <span className="paused">Paused, tap to continue</span>}
              </>
            )}
          </div>

          {!recording && phase !== 'error' && (
            <div className="frameguide" aria-hidden="true">
              <div className="g-head" />
              <div className="g-chin">
                <span>Chin above this line</span>
              </div>
            </div>
          )}

          {phase === 'countdown' && count > 0 && (
            <div className="count" aria-live="assertive">
              {count}
            </div>
          )}

          {phase === 'error' && (
            <div className="camerr" role="alert">
              <p>{problem}</p>
              <button type="button" className="btn sec" onClick={onClose}>
                Back
              </button>
            </div>
          )}

          <div className="hud">
            {problem && phase !== 'error' && (
              <p className="status warn" role="alert">
                {problem}
              </p>
            )}
            <div className="meta">
              <span className="pill">
                <span className="dot" />
                <span aria-live="off">{formatDuration(elapsed).padStart(5, '0')}</span>
              </span>
              <span className="pill mic" aria-label="Microphone level">
                <span className="lvl">
                  <i style={{ width: `${Math.round(level * 100)}%` }} />
                </span>
              </span>
              <span className="pill">
                {idx + 1} / {lines.length}
              </span>
              {!recording && (
                <>
                  <button
                    type="button"
                    className="pill"
                    aria-pressed={prefs.mode === 'roll'}
                    onClick={() => setPref({ mode: prefs.mode === 'line' ? 'roll' : 'line' })}
                  >
                    {prefs.mode === 'line' ? 'Line by line' : 'Running'}
                  </button>
                  <button
                    type="button"
                    className="pill"
                    aria-label="Smaller text"
                    onClick={() => setPref({ fs: Math.max(22, prefs.fs - 4) })}
                  >
                    A−
                  </button>
                  <button
                    type="button"
                    className="pill"
                    aria-label="Larger text"
                    onClick={() => setPref({ fs: Math.min(56, prefs.fs + 4) })}
                  >
                    A+
                  </button>
                </>
              )}
            </div>
            {prefs.mode === 'roll' && (
              <div className="speed">
                <label htmlFor="tp-speed">Speed</label>
                <input
                  id="tp-speed"
                  type="range"
                  min={15}
                  max={90}
                  value={prefs.speed}
                  onChange={(e) => setPref({ speed: Number(e.target.value) })}
                />
              </div>
            )}
            <div className="ctls">
              <button
                ref={closeRef}
                type="button"
                className="ctl"
                aria-label="Close"
                onClick={onClose}
                disabled={recording}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
              <button
                type="button"
                className="ctl"
                aria-label="Previous line"
                disabled={idx === 0}
                onClick={() => go(-1)}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              <button
                type="button"
                className="rec"
                aria-label={
                  phase === 'starting'
                    ? 'Starting camera'
                    : phase === 'error'
                      ? 'Camera not available'
                      : recording
                        ? 'Stop recording'
                        : 'Start recording'
                }
                onClick={onRec}
                disabled={phase !== 'ready' && phase !== 'recording'}
              >
                <i />
              </button>
              <button
                type="button"
                className="ctl"
                aria-label="Next line"
                disabled={idx >= lines.length - 1}
                onClick={() => go(1)}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
              <button
                type="button"
                className="ctl"
                aria-label="Switch camera"
                disabled={recording || phase === 'countdown'}
                onClick={() => setFacing((f) => (f === 'user' ? 'environment' : 'user'))}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
                  <path d="M9 13a3 3 0 0 0 5.2 2M15 12a3 3 0 0 0-5.2-2" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
